import { Shield, Zap, Clock, Briefcase, PlaneLanding, PlaneTakeoff, Compass, ArrowRight, FileSearch, CreditCard, Download, Plane } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function HeroSection() {
  const navigate = useNavigate();

  const features = [
    { icon: <Shield size={18} />, title: "Sûr & Sécurisé", desc: "Vos données sont protégées" },
    { icon: <Zap size={18} />, title: "Rapide & Simple", desc: "Procédures simplifiées en ligne" },
    { icon: <Clock size={18} />, title: "Assistance 24/7", desc: "Notre équipe est là pour vous aider" },
  ];

  const procedures = [
    {
      icon: <PlaneLanding size={24} />,
      title: "Venir au Niger",
      desc: "Procédures d'entrée et formalités requises pour entrer sur le territoire.",
      path: "/venir_au_niger",
      highlighted: true,
    },
    {
      icon: <Compass size={24} />,
      title: "Voyage Interne",
      desc: "Déplacements à l'intérieur du pays en toute sérénité.",
      path: "/procedure",
    },
    {
      icon: <PlaneTakeoff size={24} />,
      title: "Sortir du Niger",
      desc: "Procédures de sortie et documents nécessaires pour quitter le territoire.",
      path: "/sortir_du_niger",
    },
  ];

  const quickActions = [
    { icon: <FileSearch size={18} />, label: "Vérifier statut de demande", path: "/dashboard" },
    { icon: <CreditCard size={18} />, label: "Paiement en ligne", path: "/dashboard" },
    { icon: <Download size={18} />, label: "Télécharger un document", path: "/mes-docuements" },
    { icon: <Plane size={18} />, label: "Suivi de vol", path: "/dashboard" },
  ];

  return (
    <section className="relative pt-20 pb-48">
      {/* Background airport image + overlay (clipped inside hero only) */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-no-repeat bg-cover bg-center"
          style={{ backgroundImage: "url('/hero.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/25 to-transparent" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
        <div className="max-w-2xl text-white">
          <span className="inline-block rounded-full bg-white/15 backdrop-blur-sm px-4 py-1 text-xs font-semibold tracking-wider">
            NIGER VOYAGE
          </span>
          <h1 className="mt-4 text-3xl lg:text-4xl font-black leading-tight">
            Voyagez en toute sérénité avec{" "}
            <span className="text-brand-100">Niger Voyage</span>
          </h1>
          <p className="mt-3 text-white/90 text-sm md:text-base leading-relaxed max-w-lg">
            Votre plateforme officielle pour toutes les formalités de voyages entrant et sortant au Niger.
          </p>

          {/* Features */}
          <div className="mt-6 flex flex-wrap gap-5">
            {features.map((f, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  {f.icon}
                </div>
                <div>
                  <div className="text-sm font-semibold">{f.title}</div>
                  <div className="text-xs text-white/80 leading-tight">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Floating action card */}
      <div className="absolute left-1/2 -translate-x-1/2 -bottom-40 w-[94%] max-w-6xl">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            <Briefcase size={20} className="text-brand-600" />
            <h2 className="font-bold text-slate-800 text-base">Que souhaitez-vous faire ?</h2>
          </div>

          {/* Three procedure CTAs */}
          <div className="grid md:grid-cols-3 gap-4">
            {procedures.map((p, i) => (
              <button
                key={i}
                onClick={() => navigate(p.path)}
                className={`group flex items-start gap-4 border-2 rounded-xl p-4 text-left transition-all hover:shadow-md ${
                  p.highlighted
                    ? "border-brand-600 bg-brand-50"
                    : "border-slate-200 bg-white hover:border-brand-600 hover:bg-brand-50"
                }`}
              >
                <div className="w-12 h-12 rounded-lg bg-brand-600 text-white flex items-center justify-center flex-shrink-0">
                  {p.icon}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-slate-800 text-sm">{p.title}</div>
                    <ArrowRight size={16} className="text-brand-600 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-xs text-slate-500 mt-1 leading-snug">{p.desc}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Quick actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-5 mt-5 border-t border-slate-100">
            {quickActions.map((action, i) => (
              <button
                key={i}
                onClick={() => navigate(action.path)}
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-brand-600 transition-colors"
              >
                <span className="text-brand-600">{action.icon}</span>
                <span className="font-medium">{action.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
